/**
 * Delivery Management Page
 * Main dashboard for viewing and managing deliveries
 * Enhanced with transport-style inline detail view
 * Located at /delivery route (following Orders and Invoices pattern)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import type { Delivery, DeliveryFilterParams } from '@/modules/commerce/delivery/types/delivery';
import { getStageFromStatus } from '@/modules/commerce/delivery/types/delivery';
import { listDeliveries, cancelDelivery, getDeliveryStatsBySource, getDeliveryById } from '@/services/commerce/deliveryService';
import { DeliveryFilters } from './components/DeliveryFilters';
import { DeliveryTable } from './components/DeliveryTable';
import { DeliveryDetailView } from './components/DeliveryDetailView';

type ViewMode = 'list' | 'detail';

interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalElements: number;
}

export function DeliveryManagementPage() {
  const navigate = useNavigate();

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedDelivery, setSelectedDelivery] = useState<Delivery | null>(null);

  // List state
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DeliveryFilterParams>({});
  const [pagination, setPagination] = useState<PaginationState>({
    currentPage: 0,
    pageSize: 50,  // Show more records per page
    totalPages: 0,
    totalElements: 0,
  });
  const [stats, setStats] = useState<Record<string, number>>({});
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch deliveries
  const fetchDeliveries = useCallback(async (page: number = 0, filterParams: DeliveryFilterParams = {}) => {
    try {
      setLoading(true);
      setError(null);

      const response = await listDeliveries(filterParams, page, pagination.pageSize);

      // Enhance deliveries with calculated stage
      const enhancedDeliveries = response.content.map((d) => ({
        ...d,
        stage: d.stage ?? getStageFromStatus(d.status),
      }));

      setDeliveries(enhancedDeliveries);
      setPagination({
        currentPage: response.currentPage,
        pageSize: response.pageSize,
        totalPages: response.totalPages,
        totalElements: response.totalElements,
      });
    } catch (err: any) {
      console.error('Failed to fetch deliveries:', err);
      setError(err.message || 'Failed to load deliveries');
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize]);

  // Fetch statistics
  const fetchStats = useCallback(async () => {
    try {
      const statsData = await getDeliveryStatsBySource();
      setStats(statsData);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  // Fetch single delivery detail
  const fetchDeliveryDetail = useCallback(async (deliveryId: number) => {
    try {
      setDetailLoading(true);
      const delivery = await getDeliveryById(deliveryId);
      setSelectedDelivery({
        ...delivery,
        stage: delivery.stage ?? getStageFromStatus(delivery.status),
      });
    } catch (err: any) {
      console.error('Failed to fetch delivery detail:', err);
      setError(err.message || 'Failed to load delivery details');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchDeliveries(0, filters).then(() => setLastRefreshed(new Date()));
    fetchStats();
  }, []);

  // Handle filter changes
  const handleFiltersChange = (newFilters: DeliveryFilterParams) => {
    setFilters(newFilters);
    fetchDeliveries(0, newFilters);
  };

  // Handle search
  const handleSearch = (query: string) => {
    const newFilters = { ...filters, search: query };
    setFilters(newFilters);
    fetchDeliveries(0, newFilters);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    fetchDeliveries(page, filters);
  };

  // Handle view delivery (inline)
  const handleViewDelivery = (delivery: Delivery) => {
    setSelectedDelivery(delivery);
    setViewMode('detail');
    // Optionally fetch fresh data
    fetchDeliveryDetail(delivery.id);
  };

  // Handle back to list
  const handleBackToList = () => {
    setViewMode('list');
    setSelectedDelivery(null);
    // Refresh list to get any updates
    fetchDeliveries(pagination.currentPage, filters);
  };

  // Handle refresh detail
  const handleRefreshDetail = async () => {
    if (selectedDelivery) {
      try {
        await fetchDeliveryDetail(selectedDelivery.id);
        toast.success('Delivery details refreshed');
      } catch {
        toast.error('Failed to refresh delivery details');
      }
    }
  };

  // Handle edit delivery
  const handleEditDelivery = (delivery: Delivery) => {
    navigate(`/sales/delivery/${delivery.id}/edit`, { state: { delivery } });
  };

  // Handle cancel delivery
  const handleCancelDelivery = async (delivery: Delivery) => {
    if (!confirm(`Are you sure you want to cancel delivery ${delivery.requestNumber || delivery.deliveryNumber || delivery.id}?`)) {
      return;
    }

    try {
      setLoading(true);
      await cancelDelivery(delivery.id, 'Cancelled by user');
      setError(null);
      await fetchDeliveries(pagination.currentPage, filters);
      alert('Delivery cancelled successfully');
    } catch (err: any) {
      setError(err.message || 'Failed to cancel delivery');
      console.error('Failed to cancel delivery:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      await Promise.all([
        fetchDeliveries(pagination.currentPage, filters),
        fetchStats(),
      ]);
      setLastRefreshed(new Date());
      toast.success('Delivery list refreshed successfully');
    } catch {
      toast.error('Failed to refresh delivery list');
    } finally {
      setRefreshing(false);
    }
  };

  // Handle create new delivery
  const handleCreateNew = () => {
    navigate('/sales/delivery/new');
  };

  // Render detail view
  if (viewMode === 'detail' && selectedDelivery) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          <DeliveryDetailView
            delivery={selectedDelivery}
            loading={detailLoading}
            onBack={handleBackToList}
            onRefresh={handleRefreshDetail}
            onEdit={handleEditDelivery}
            onStatusUpdate={() => {
              // Refresh stats when status changes
              fetchStats();
            }}
          />
        </div>
      </div>
    );
  }

  // Render list view
  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Delivery Management</h1>
              <p className="text-gray-600 mt-1">Track and manage all deliveries through their lifecycle</p>
            </div>
            <button
              onClick={handleCreateNew}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Delivery Request
            </button>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-bold">Error</p>
                  <p className="text-sm">{error}</p>
                  <button
                    onClick={handleRefresh}
                    className="text-red-600 hover:text-red-800 underline font-medium mt-2"
                  >
                    Retry
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                </svg>
              </div>
              <div>
                <div className="text-gray-600 text-sm">Total Deliveries</div>
                <div className="text-2xl font-bold text-green-600">{pagination.totalElements}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <div className="text-gray-600 text-sm">From UrjaVyapar</div>
                <div className="text-2xl font-bold text-blue-600">{stats.UrjaVyapar || 0}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </div>
              <div>
                <div className="text-gray-600 text-sm">From Transport</div>
                <div className="text-2xl font-bold text-orange-600">{stats.Transport || 0}</div>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <div className="text-gray-600 text-sm">Showing</div>
                <div className="text-2xl font-bold text-purple-600">{deliveries.length}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <DeliveryFilters
          onFiltersChange={handleFiltersChange}
          onSearch={handleSearch}
        />

        {/* Table */}
        <DeliveryTable
          deliveries={deliveries}
          loading={loading}
          onView={handleViewDelivery}
          onEdit={handleEditDelivery}
          onCancel={handleCancelDelivery}
        />

        {/* Pagination */}
        <div className="mt-6 flex justify-between items-center bg-white p-4 rounded-lg shadow">
          <div className="text-gray-600 text-sm">
            <span>Showing {String(deliveries.length)} of {String(pagination.totalElements)} deliveries</span>
            {lastRefreshed && (
              <span className="ml-3 text-gray-400">
                Updated: {lastRefreshed.toLocaleTimeString('en-IN')}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(Math.max(0, pagination.currentPage - 1))}
              disabled={pagination.currentPage === 0 || loading}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-300 transition flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>

            {/* Page numbers */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                const startPage = Math.max(0, Math.min(pagination.currentPage - 2, pagination.totalPages - 5));
                const pageNum = startPage + i;
                if (pageNum < 0 || pageNum >= pagination.totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded-lg transition ${
                      pageNum === pagination.currentPage
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {String(pageNum + 1)}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(Math.min(pagination.totalPages - 1, pagination.currentPage + 1))}
              disabled={pagination.currentPage >= pagination.totalPages - 1 || loading}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-300 transition flex items-center gap-1"
            >
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>

            <button
              onClick={handleRefresh}
              disabled={loading || refreshing}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-300 transition flex items-center gap-2"
              title={lastRefreshed ? `Last refreshed: ${lastRefreshed.toLocaleTimeString('en-IN')}` : 'Refresh'}
            >
              <svg className={`w-5 h-5 ${(loading || refreshing) ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {refreshing && <span className="text-xs">Refreshing...</span>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeliveryManagementPage;
